import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { LANGUAGES } from '../i18n/translations';

const LangSwitch = ({ className = '' }) => {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="language-selector">
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-switch-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-2 py-1 text-xs uppercase tracking-widest transition-colors duration-300 ${
            lang === l.code ? 'text-terracotta-dark font-medium' : 'text-emerald/60 hover:text-emerald'
          }`}
          aria-label={l.name}
        >
          {l.code}
        </button>
      ))}
    </div>
  );
};

export default function Header() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: '/', label: t.nav.home, id: 'home' },
    { to: '/servizi', label: t.nav.servizi, id: 'servizi' },
    { to: '/team-building', label: t.nav.tb, id: 'team-building' },
    { to: '/christmas-party', label: t.nav.xmas, id: 'christmas-party' },
    { to: '/location', label: t.nav.location, id: 'location' },
    { to: '/gallery', label: t.nav.gallery, id: 'gallery' },
    { to: '/chi-siamo', label: t.nav.about, id: 'chi-siamo' },
    { to: '/contatti', label: t.nav.contatti, id: 'contatti' },
  ];

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-ivory/90 backdrop-blur-md border-b border-emerald/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" aria-label="Gustavo 28 - Home">
            <img src="/images/logo-ivory.png" alt="Gustavo 28" className="h-6 md:h-7 w-auto" data-testid="header-logo" />
          </Link>
          <nav className="hidden lg:flex items-center gap-5" data-testid="main-nav">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={`text-sm tracking-wide whitespace-nowrap transition-colors duration-300 hover:text-terracotta-dark ${
                  location.pathname === l.to ? 'text-terracotta-dark font-medium' : 'text-emerald'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            <LangSwitch />
            <Link
              to="/contatti"
              data-testid="header-cta-quote"
              className="rounded-full bg-emerald text-ivory text-sm whitespace-nowrap px-6 py-2.5 transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5"
            >
              {t.nav.cta}
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-emerald p-2"
            onClick={() => setOpen(true)}
            aria-label="Menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-emerald-deep grain flex flex-col"
          >
            <div className="h-20 px-6 flex items-center justify-between">
              <img src="/images/logo-ivory-transparent.png" alt="Gustavo 28" className="h-8 w-auto" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-ivory p-2" aria-label="Chiudi">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="block font-serif text-4xl text-ivory py-2 hover:text-terracotta transition-colors duration-300"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <LangSwitch className="[&_button]:text-ivory/70 [&_button]:text-sm" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
